/**
 * Password Validation Utility
 *
 * Provides strong password validation for Hesvara authentication.
 * Requirements:
 * - Minimum 8 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 number
 * - At least 1 special character (!@#$%^&*)
 */

export interface PasswordRequirement {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  requirements: {
    id: string;
    label: string;
    met: boolean;
  }[];
}

export type PasswordStrength = 'weak' | 'medium' | 'strong';

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: 'minLength',
    label: 'At least 8 characters',
    test: (password: string) => password.length >= 8,
  },
  {
    id: 'uppercase',
    label: 'At least 1 uppercase letter',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    id: 'lowercase',
    label: 'At least 1 lowercase letter',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    id: 'number',
    label: 'At least 1 number',
    test: (password: string) => /[0-9]/.test(password),
  },
  {
    id: 'special',
    label: 'At least 1 special character (!@#$%^&*)',
    test: (password: string) => /[!@#$%^&*]/.test(password),
  },
];

/**
 * Validates a password against all requirements
 */
export function validatePassword(password: string): PasswordValidationResult {
  const requirements = PASSWORD_REQUIREMENTS.map((req) => ({
    id: req.id,
    label: req.label,
    met: req.test(password),
  }));

  const errors = requirements
    .filter((req) => !req.met)
    .map((req) => req.label);

  return {
    isValid: errors.length === 0,
    errors,
    requirements,
  };
}

/**
 * Calculates password strength based on met requirements
 */
export function getPasswordStrength(password: string): PasswordStrength {
  if (!password) {
    return 'weak';
  }

  const result = validatePassword(password);
  const metCount = result.requirements.filter((req) => req.met).length;
  const totalCount = result.requirements.length;

  if (metCount === totalCount) {
    return 'strong';
  } else if (metCount >= 3) {
    return 'medium';
  } else {
    return 'weak';
  }
}

/**
 * Returns a human-readable strength label
 */
export function getStrengthLabel(strength: PasswordStrength): string {
  switch (strength) {
    case 'strong':
      return 'Strong';
    case 'medium':
      return 'Medium';
    case 'weak':
      return 'Weak';
  }
}
